import { Children, type ReactNode } from 'react';
import { isElement } from 'react-is';

import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import type { AnnotationsTooltipConfig } from '../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsMarkerConfig } from '../../core/components/annotations/types/annotations-marker-props.js';
import type { AnnotationsTrackConfig } from '../../core/components/annotations/types/annotations-track-props.js';
import { populateMissingMarkerProps } from '../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../core/components/annotations/utils/populate-missing-track-props.js';
import { ChartInteractions } from '../../core/slots/chart-interactions/ChartInteractions.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import { iterateChartInteractionsSlots } from '../../core/utils/iterate-chart-interactions-slots.js';
import { Annotations } from '../slots/annotations/Annotations.js';
import { Marker } from '../slots/annotations/Marker.js';
import { AnnotationsTooltip } from '../slots/annotations/Tooltip.js';
import { Track } from '../slots/annotations/Track.js';
import { Legend } from '../slots/Legend.js';
import { Select } from '../slots/Select.js';
import { Threshold } from '../slots/Threshold.js';
import { Tooltip } from '../slots/Tooltip.js';
import { XAxis } from '../slots/XAxis.js';
import { YAxis } from '../slots/YAxis.js';
import {
  HistogramChartSlots,
  HistogramChartToolbarConfig,
} from '../types/histogram.js';

export const iterateSlots = (children: ReactNode): HistogramChartSlots => {
  let slots: HistogramChartSlots = {
    legend: {},
    xAxis: {},
    yAxis: [],
    tooltip: {},
    toolbar: {},
    interactions: {},
    thresholds: [],
  };

  let intents: IntentProps[] | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, ChartInteractions)) {
      const { children, ...previousInteractions } = child.props;

      const interactions = iterateChartInteractionsSlots(children);

      slots = {
        ...slots,
        interactions: {
          ...previousInteractions,
          ...interactions,
        },
      };
    } else if (isSlot(child, XAxis)) {
      slots = {
        ...slots,
        xAxis: child.props,
      };
    } else if (isSlot(child, YAxis)) {
      slots.yAxis.push(child.props);
    } else if (isSlot(child, Legend)) {
      slots = {
        ...slots,
        legend: child.props,
      };
    } else if (isSlot(child, Tooltip)) {
      slots = {
        ...slots,
        tooltip: child.props,
      };
    } else if (isSlot(child, ChartToolbar)) {
      const { children, ...toolbar } = child.props;
      const childrenToolbar = iterateChartToolbarSlots(children);

      slots = {
        ...slots,
        toolbar: {
          enabled: true,
          ...toolbar,
          ...childrenToolbar,
        },
      };
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Annotations)) {
      const { children, ...annotationsProps } = child.props;

      const { tracks, tooltip } = iterateTrackSlots(children);

      const annotations = {
        ...annotationsProps,
        tracks,
        tooltip,
      };

      slots = {
        ...slots,
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
        // @ts-ignore Strict mode transition - needs to be investigated
        annotations,
      };
    } else if (isSlot(child, Threshold)) {
      slots.thresholds.push(child.props);
    } else if (isSlot(child, Select)) {
      slots = {
        ...slots,
        select: child.props,
      };
    }
  });

  if (Object.keys(slots.toolbar).length > 0) {
    slots = {
      ...slots,
      toolbar: {
        ...slots.toolbar,
        intents,
      },
    };
  }

  return slots;
};

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (
  children: ReactNode,
): HistogramChartToolbarConfig => {
  let toolbar = {};

  Children.forEach(children, (childToolbar) => {
    if (isSlot(childToolbar, DownloadData)) {
      toolbar = {
        ...toolbar,
        downloadData: { enabled: true },
      };
    }
  });

  return toolbar;
};

/**
 * Generate config based on the Tracks and Markers slots
 * @param children - Annotations children
 * @returns tracks configuration including the markers
 */
const iterateTrackSlots = (children: ReactNode) => {
  const tracks: AnnotationsTrackConfig[] = [];

  let tooltip: AnnotationsTooltipConfig | undefined = {
    template: undefined,
    hidden: false,
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Track)) {
      const { children, ...trackProps } = child.props;
      const currentTrack = {
        ...trackProps,
        markers: [] as AnnotationsMarkerConfig[],
      };

      Children.forEach(children, (child) => {
        if (isElement(child)) {
          if (isSlot(child, Marker)) {
            const marker = populateMissingMarkerProps(child.props, trackProps);

            currentTrack.markers.push(marker);
          }
        }
      });

      const track = populateMissingTrackProps(currentTrack);
      tracks.push(track);
    } else if (isSlot(child, AnnotationsTooltip)) {
      const { children, hidden } = child.props;
      tooltip = {
        template: children,
        hidden: hidden ?? false,
      };
    }
  });

  return { tracks, tooltip };
};
